/**
 * BusinessDatePicker — professional date input for scheduling connections.
 *
 * Features:
 *  - US federal + Texas-observed holidays disabled (with tooltip explaining)
 *  - Optional weekend disabling (default: Sundays disabled)
 *  - Bloomberg/Direct-Energy inspired minimalist UI
 *  - Keyboard navigable, month/year header, "today" and "clear" shortcuts
 *  - Fully controlled: value + onChange (ISO YYYY-MM-DD)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { CaretLeft, CaretRight, Calendar as CalIcon, X } from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

// ── Holiday calendar ────────────────────────────────────────────────────────
// US federal + Texas-observed holidays. Each returns YYYY-MM-DD.
function easterSunday(y) {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

function nthWeekdayOfMonth(year, month, weekday, n) {
  // month: 1-12, weekday: 0=Sun..6=Sat, n: 1..5 or -1 for last
  if (n === -1) {
    const last = new Date(year, month, 0);
    let d = last.getDate();
    while (new Date(year, month - 1, d).getDay() !== weekday) d--;
    return d;
  }
  const first = new Date(year, month - 1, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return 1 + offset + (n - 1) * 7;
}

function pad2(n) { return String(n).padStart(2, "0"); }

export function getHolidays(year) {
  const H = {};
  const set = (m, d, name) => { H[`${year}-${pad2(m)}-${pad2(d)}`] = name; };

  // Fixed dates
  set(1, 1, "Año Nuevo");
  set(7, 4, "Independencia (US)");
  set(11, 11, "Día de los Veteranos");
  set(12, 25, "Navidad");
  set(6, 19, "Juneteenth");

  // Floating
  set(1, nthWeekdayOfMonth(year, 1, 1, 3), "Martin Luther King Jr. Day");
  set(2, nthWeekdayOfMonth(year, 2, 1, 3), "Presidents' Day");
  set(5, nthWeekdayOfMonth(year, 5, 1, -1), "Memorial Day");
  set(9, nthWeekdayOfMonth(year, 9, 1, 1), "Labor Day");
  set(10, nthWeekdayOfMonth(year, 10, 1, 2), "Columbus Day");
  set(11, nthWeekdayOfMonth(year, 11, 4, 4), "Thanksgiving");
  // Day after Thanksgiving (also observed off)
  const thx = nthWeekdayOfMonth(year, 11, 4, 4);
  set(11, thx + 1, "Día después de Thanksgiving");

  // Easter-related (Texas observance varies but Good Friday is common)
  const easter = easterSunday(year);
  // Good Friday = 2 days before Easter Sunday
  const gfDate = new Date(year, easter.month - 1, easter.day - 2);
  set(gfDate.getMonth() + 1, gfDate.getDate(), "Viernes Santo");

  return H;
}

// ── Component ───────────────────────────────────────────────────────────────
export function BusinessDatePicker({
  value,           // ISO string or ""
  onChange,        // (iso: string) => void
  minDate,         // ISO
  maxDate,         // ISO
  disableWeekends = { saturday: false, sunday: true },
  lang = "es",
  className = "",
  placeholder,
  "data-testid": testid,
}) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() => {
    if (value) return new Date(value + "T12:00:00");
    return new Date();
  });
  const rootRef = useRef(null);

  useEffect(() => {
    if (value) setCursor(new Date(value + "T12:00:00"));
  }, [value]);

  // Radix Popover handles outside-click and Esc dismissal via portals — no manual handler needed.

  const monthNames = lang === "en"
    ? ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
    : ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const dayLabels = lang === "en"
    ? ["S", "M", "T", "W", "T", "F", "S"]
    : ["D", "L", "M", "M", "J", "V", "S"];

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const holidays = useMemo(() => getHolidays(year), [year]);

  const grid = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const startWeekday = firstOfMonth.getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    // Leading blank cells
    for (let i = 0; i < startWeekday; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${year}-${pad2(month + 1)}-${pad2(d)}`;
      const jsDate = new Date(year, month, d);
      const weekday = jsDate.getDay();
      const isSat = weekday === 6;
      const isSun = weekday === 0;
      const holidayName = holidays[iso];
      let disabled = false;
      let reason = null;
      if (isSat && disableWeekends?.saturday) { disabled = true; reason = lang === "en" ? "Saturday — closed" : "Sábado — cerrado"; }
      if (isSun && disableWeekends?.sunday) { disabled = true; reason = lang === "en" ? "Sunday — closed" : "Domingo — cerrado"; }
      if (holidayName) { disabled = true; reason = holidayName; }
      if (minDate && iso < minDate) disabled = true;
      if (maxDate && iso > maxDate) disabled = true;
      days.push({ iso, d, weekday, disabled, reason, holidayName, isSat, isSun });
    }
    return days;
  }, [year, month, holidays, disableWeekends?.saturday, disableWeekends?.sunday, minDate, maxDate, lang]);

  const today = new Date();
  const todayIso = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;

  const label = value
    ? (() => {
        const dt = new Date(value + "T12:00:00");
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${mm}/${dd}/${dt.getFullYear()}`;
      })()
    : (placeholder || (lang === "en" ? "MM/DD/YYYY" : "MM/DD/AAAA"));

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full h-10 px-3 flex items-center justify-between gap-2 text-sm rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 hover:border-slate-400 dark:hover:border-slate-500 focus:outline-none focus:border-slate-900 dark:focus:border-slate-300 transition-colors"
            data-testid={testid}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <span className="flex items-center gap-2 min-w-0 truncate">
              <CalIcon size={14} className="text-slate-500 dark:text-slate-400 shrink-0" />
              <span className={`truncate ${value ? "" : "text-slate-400"}`}>{label}</span>
            </span>
            {value && (
              <span
                role="button"
                aria-label="Clear"
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                className="text-slate-400 hover:text-rose-500 shrink-0 cursor-pointer"
              >
                <X size={13} />
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="w-[340px] p-3 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl z-[9999]">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setCursor(new Date(year, month - 1, 1))}
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
              aria-label="Prev month"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <div className="flex items-center gap-1.5">
              <select
                value={month}
                onChange={(e) => setCursor(new Date(year, Number(e.target.value), 1))}
                className="text-xs font-semibold uppercase tracking-wider bg-transparent border-0 focus:outline-none cursor-pointer text-slate-800 dark:text-slate-100"
              >
                {monthNames.map((n, i) => <option key={n} value={i}>{n}</option>)}
              </select>
              <input
                type="number"
                value={year}
                min={2020}
                max={2035}
                onChange={(e) => setCursor(new Date(Number(e.target.value), month, 1))}
                className="w-16 text-xs font-mono bg-transparent border-0 focus:outline-none text-slate-800 dark:text-slate-100"
              />
            </div>
            <button
              type="button"
              onClick={() => setCursor(new Date(year, month + 1, 1))}
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
              aria-label="Next month"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {dayLabels.map((l, i) => (
              <div
                key={i}
                className={`text-center text-[10px] font-semibold uppercase tracking-wider py-1 ${
                  i === 0 || i === 6 ? "text-slate-300 dark:text-slate-600" : "text-slate-500 dark:text-slate-400"
                }`}
              >
                {l}
              </div>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {grid.map((cell, idx) => {
              if (!cell) return <div key={idx} />;
              const isSelected = value === cell.iso;
              const isToday = cell.iso === todayIso;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  disabled={cell.disabled}
                  onClick={() => { if (!cell.disabled) { onChange(cell.iso); setOpen(false); } }}
                  title={cell.reason || ""}
                  className={`
                    relative h-9 text-xs rounded transition-all
                    ${cell.disabled
                      ? "text-slate-300 dark:text-slate-700 cursor-not-allowed line-through decoration-slate-300 dark:decoration-slate-700"
                      : "text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"}
                    ${isSelected ? "!bg-slate-900 dark:!bg-amber-500 !text-white font-semibold" : ""}
                    ${isToday && !isSelected ? "ring-1 ring-amber-500 ring-inset" : ""}
                    ${cell.holidayName && !isSelected ? "bg-rose-50 dark:bg-rose-950/30" : ""}
                  `}
                >
                  {cell.d}
                  {cell.holidayName && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-rose-500 dark:bg-rose-400" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend + shortcuts */}
          <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> {lang === "en" ? "Holiday" : "Feriado"}</span>
              <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full ring-1 ring-amber-500" /> {lang === "en" ? "Today" : "Hoy"}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setCursor(today); onChange(todayIso); setOpen(false); }}
                className="text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white font-medium"
              >
                {lang === "en" ? "Today" : "Hoy"}
              </button>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <button
                type="button"
                onClick={() => { onChange(""); setOpen(false); }}
                className="text-slate-500 hover:text-rose-500"
              >
                {lang === "en" ? "Clear" : "Limpiar"}
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
